import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { brandColors } from '@liminal/brand/palette';
import { borderRadius } from '@liminal/brand/scale';
import { budgetRemaining, budgetUsage, usdText } from '@/lib/rewards';

const SPENT_COLOR = brandColors.black;
const HELD_COLOR = brandColors.orangeLight;

interface BudgetUsageProps {
  readonly limitUsd?: string;
  readonly spentUsd?: string;
  readonly reservedUsd?: string;
  /** `full` adds a legend under the bar naming each part. */
  readonly detail?: 'compact' | 'full';
}

function Swatch({ color }: { readonly color: string }) {
  return (
    <Box
      component="span"
      sx={{
        width: 10,
        height: 10,
        borderRadius: `${borderRadius.sm}px`,
        bgcolor: color,
        border: `1px solid ${webColors.border.medium}`,
        flexShrink: 0,
      }}
    />
  );
}

/**
 * A budget as a bar: spent, held for payments still settling, and what is
 * left of the limit. A budget whose figures cannot be read says so instead of
 * drawing a believable bar.
 */
export function BudgetUsage({ limitUsd = '', spentUsd = '', reservedUsd = '', detail = 'compact' }: BudgetUsageProps) {
  const usage = budgetUsage(limitUsd, spentUsd, reservedUsd);
  const remaining = budgetRemaining(limitUsd, spentUsd, reservedUsd);
  if (!usage || remaining === null) {
    return (
      <Typography variant="body2" color="error">
        Figures unreadable
      </Typography>
    );
  }

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {`${usdText(remaining)} left`}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {`of ${usdText(limitUsd)}`}
        </Typography>
      </Stack>
      <Box
        role="img"
        aria-label={`${usdText(spentUsd)} spent, ${usdText(reservedUsd)} held, ${usdText(remaining)} left of ${usdText(limitUsd)}`}
        sx={{
          display: 'flex',
          height: 8,
          borderRadius: `${borderRadius.full}px`,
          overflow: 'hidden',
          bgcolor: webColors.surface.secondary,
          border: `1px solid ${webColors.border.light}`,
        }}
      >
        <Box sx={{ width: `${usage.spentPercent}%`, bgcolor: SPENT_COLOR }} />
        <Box sx={{ width: `${usage.heldPercent}%`, bgcolor: HELD_COLOR }} />
      </Box>
      {detail === 'full' && (
        <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center">
            <Swatch color={SPENT_COLOR} />
            <Typography variant="caption">{`${usdText(spentUsd)} paid back`}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Swatch color={HELD_COLOR} />
            <Typography variant="caption">{`${usdText(reservedUsd)} held for payments in progress`}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Swatch color={webColors.surface.secondary} />
            <Typography variant="caption">{`${usdText(remaining)} left`}</Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
